import { AuthenticationResult, InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import { IOAuthCredential } from "../models/IOAuthCredential";
import { IOAuthAccount } from "../models/IOAuthAccount";

/**
 * The contract for the OAuth service
 */
export interface IOAuthService {
  /**
   * Gets the OAuth accounts in the cache
   *
   * @returns the accounts
   */
  getAccountsAsync(): Promise<IOAuthAccount[]>;

  /**
   * Obtains token(s), interactively if needed
   *
   * @param homeAccountId - the ID for a specific account for which the token(s) should be obtained
   * @param silent - if true, acquires the token silently and throws otherwise
   * @returns the token
   */
  acquireTokensAsync(homeAccountId?: string, silent?: boolean): Promise<IOAuthCredential>;

  /**
   * Logs out of an account.
   *
   * @param homeAccountId - The account ID.
   */
  logoutAccountAsync(homeAccountId: string): Promise<void>;
}

/**
 * The OAuth service for dealing with accounts and tokens
 */
export class OAuthService implements IOAuthService {
  /**
   * The MSAL application object
   */
  private appPromise: Promise<PublicClientApplication>;

  /**
   * Initializes a new instance of the OAuthService class.
   *
   * @param authority - the Azure AD authority
   * @param clientId - the client ID
   * @param redirectUri - the redirect URI
   * @param oauthScope - the OAuth scope to acquire tokens for
   */
  public constructor(authority: string, clientId: string, redirectUri: string, private oauthScope: string) {
    this.appPromise = OAuthService.initializeApplication(authority, clientId, redirectUri);
  }

  /**
   * Gets the OAuth accounts in the cache
   *
   * @returns the accounts
   */
  public async getAccountsAsync(): Promise<IOAuthAccount[]> {
    const accountsWithTokens: IOAuthAccount[] = [];

    const app = await this.appPromise;

    const accounts = app.getAllAccounts();
    if (!accounts) {
      return Promise.resolve([]);
    }

    for (const account of accounts) {
      accountsWithTokens.push({
        homeAccountId: account.homeAccountId,
        username: account.username,
        environment: account.environment,
        tenantId: account.tenantId,
      });
    }

    return Promise.resolve(accountsWithTokens);
  }

  /**
   * Obtains token(s), interactively if needed
   *
   * @param homeAccountId - the ID for a specific account for which the token(s) should be obtained
   * @param silent - if true, acquires the token silently and throws otherwise
   * @returns the token
   */
  public async acquireTokensAsync(homeAccountId: string | undefined = undefined, silent = false): Promise<IOAuthCredential> {
    const app = await this.appPromise;

    // Start with the account provided if we can find it in the cache
    let account = homeAccountId ? app.getAccount({ homeAccountId }) : null;

    let result: AuthenticationResult | null = null;

    // If we've already obtained an account
    if (account) {
      try {
        result = await app.acquireTokenSilent({
          account: account,
          scopes: [this.oauthScope],
        });

        account = result.account;
      } catch (error) {
        // For silent mode, rethrow all errors
        // For interactive mode, rethrow all errors except ones that claim interaction is required.
        if (silent || !(error instanceof InteractionRequiredAuthError)) {
          throw error;
        }
      }
    }

    if (!silent && !result?.accessToken) {
      result = await app.acquireTokenPopup({
        scopes: [this.oauthScope],
        prompt: "login",
      });

      account = result.account;
    }

    // This shouldn't normally happen as acquireTokenSilent or acquireTokenPopup should throw
    if (!result?.accessToken) {
      throw new Error("Unable to obtain access token");
    }

    // I don't actually know of a scenario where this happens, but account is marked nullable
    if (!result.account) {
      throw new Error("Account not available from AuthenticationResult");
    }

    return {
      ...result.account,
      accessToken: result.accessToken,
    };
  }

  /**
   * Logs out of an account.
   *
   * @param homeAccountId - The account ID.
   */
  public async logoutAccountAsync(homeAccountId: string): Promise<void> {
    const app = await this.appPromise;
    const account = homeAccountId ? app.getAccount({ homeAccountId }) : null;
    if (!account) {
      return;
    }

    await app.logoutPopup({
      account,
    });
  }

  /**
   * Initializes the MSAL application
   *
   * @param authority - The Azure AD authority
   * @param clientId - the client ID
   * @param redirectUri - the redirect URI
   * @returns - the initialized MSAL application
   */
  private static async initializeApplication(authority: string, clientId: string, redirectUri: string): Promise<PublicClientApplication> {
    const app = new PublicClientApplication({
      auth: {
        clientId,
        authority,
        redirectUri,
      },
    });

    await app.initialize();

    return app;
  }
}
